import {SCHEMATIC_HIERARCHY_RENDERING_MODE, type SchematicHierarchyRenderingMode, type SchematicPage, type SchematicPageGroup, type SchematicRenderingHints} from '../../../../generated/crysknife/types.ts';

function pushToBucket<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const bucket = map.get(key);
  if (bucket) {
    bucket.push(value);
  } else {
    map.set(key, [value]);
  }
}

export type SchematicHierarchyGroup = {
  groupId: string;
  displayName: string;
  /**
   * Whether this group should render without a row of its own. Set from the
   * schematic's `rendering_hints.hide_group_nodes` and repeated on every node so
   * the recursive renderers can read it without prop drilling.
   */
  hideGroupNodes: boolean;
  /**
   * From the group's `origin_id`: equal across groups instantiated from the same
   * hierarchical block, which is how repeated instances are matched up. `null`
   * for formats that do not supply it.
   */
  originId: string | null;
  pages: SchematicHierarchyPage[];
  unanchoredChildGroups: SchematicHierarchyGroup[];
};

export type SchematicHierarchyPage = {
  pageId: string;
  name: string;
  /**
   * Optional display-only name for an instance position after flattening. Keeps
   * the original page name intact for diff mapping while preserving labels like
   * TILE-1/TILE-2 in grouped views.
   */
  instanceLabel?: string;
  /**
   * Pages this row stands for after instances of a block were combined. They
   * have no row of their own, so anything showing this page's changes must show
   * theirs too — a change nobody can reach has left the review.
   */
  mergedPageIds: string[];
  /** Whether this page is present only on the base side (a sheet the diff
   * deleted). It still gets a row, but it does not describe the instance's
   * current shape. */
  deletedFromHead?: boolean;
  childGroups: SchematicHierarchyGroup[];
};

export type SchematicHierarchyInputPage = Pick<SchematicPage, 'id' | 'name' | 'source_path' | 'ui_group_id'>;

export function unmatchedBaseHierarchyPages(
  headPages: readonly SchematicHierarchyInputPage[],
  basePages: readonly SchematicHierarchyInputPage[],
): SchematicHierarchyInputPage[] {
  const identity = (page: SchematicHierarchyInputPage): string | null =>
    page.source_path ? `${page.source_path}\u0000${page.name}` : null;
  const headIdentities = new Set(headPages.map(identity).filter((key): key is string => key !== null));
  return basePages.filter((page) => {
    const key = identity(page);
    return key === null || !headIdentities.has(key);
  });
}

export type ResolvedRenderingHints = {
  hideGroupNodes: boolean;
  renderMode: SchematicHierarchyRenderingMode;
};

/**
 * Resolve the rendering hints for a diff, preferring head over base. A deleted
 * project has no head convert, and converts rendered before crysknife emitted
 * the hints have neither, so both fall back to showing group rows.
 */
export function resolveRenderingHints(
  head: SchematicRenderingHints | undefined,
  base: SchematicRenderingHints | undefined,
): ResolvedRenderingHints {
  // Head describes the diff's current state, so it decides the mode; base only
  // stands in when there is no head. A mode this build does not know falls back
  // to the nested view rather than failing.
  const mode = head?.hierarchy_render_mode ?? base?.hierarchy_render_mode;
  const known = Object.values(SCHEMATIC_HIERARCHY_RENDERING_MODE).includes(mode as SchematicHierarchyRenderingMode);

  return {
    hideGroupNodes: head?.hide_group_nodes ?? base?.hide_group_nodes ?? false,
    renderMode: known ? mode as SchematicHierarchyRenderingMode : SCHEMATIC_HIERARCHY_RENDERING_MODE.FULL,
  };
}

/**
 * Depth-first search over a hierarchy group: true if any page in the group or its
 * descendants satisfies `predicate`.
 *
 * By default only pages with a row of their own count. Instances combined into a
 * row are reachable through it but never open on their own, so a caller asking
 * what the tree can show must not count them — a row that lights up for a page
 * it will not display sends the reader somewhere with nothing to see. Pass
 * `includeMerged` to ask the other question: whether the hierarchy accounts for
 * a page at all, where a combined instance does count.
 */
export function groupContainsPage(
  group: SchematicHierarchyGroup,
  predicate: (pageId: string) => boolean,
  {includeMerged = false}: {includeMerged?: boolean} = {},
): boolean {
  const options = {includeMerged};
  for (const page of group.pages) {
    if (predicate(page.pageId)) return true;
    if (includeMerged && page.mergedPageIds.some(predicate)) return true;
    if (page.childGroups.some((g) => groupContainsPage(g, predicate, options))) return true;
  }
  return group.unanchoredChildGroups.some((g) => groupContainsPage(g, predicate, options));
}

/**
 * Build a page-oriented hierarchy tree from crysknife's `page_groups` map and the
 * schematic's pages. Each returned root represents a group with `parent: null`.
 * Child groups nest under the specific page their block symbol lives on, via
 * `parent.reference_in_parent.parent_page_id`. Child groups without a resolvable
 * parent page hang off the parent group as `unanchoredChildGroups`.
 *
 * When base-commit data is provided, deleted pages (present in base but absent
 * from head) are placed into their base group, and base-only groups are included
 * so the hierarchy covers the full diff universe. Head data takes precedence.
 *
 * Returns `[]` when no usable hierarchy is present so the caller can fall back to
 * a flat page list.
 *
 * `hideGroupNodes` is stamped onto every node rather than affecting the tree's
 * shape; it tells the renderers whether a group is worth a row. Formats that
 * carry instance identity on the group name (OrCad, DE-HDL, SysCap) pass `false`
 * so repeated blocks stay distinguishable.
 */
export function buildSchematicHierarchy(
  pages: readonly SchematicHierarchyInputPage[],
  pageGroups: Readonly<Record<string, SchematicPageGroup>> | null | undefined,
  basePages?: readonly SchematicHierarchyInputPage[] | null,
  basePageGroups?: Readonly<Record<string, SchematicPageGroup>> | null,
  hideGroupNodes: boolean = false,
): SchematicHierarchyGroup[] {
  // Merge page_groups: head takes precedence, base fills in removed groups.
  const mergedPageGroups: Record<string, SchematicPageGroup> = {...basePageGroups, ...pageGroups};
  if (Object.keys(mergedPageGroups).length === 0) {
    return [];
  }

  const headPageIds = new Set(pages.filter((p) => p.id).map((p) => p.id));

  const pagesByGroupId = new Map<string, SchematicHierarchyInputPage[]>();
  for (const page of pages) {
    if (!page.id || !page.ui_group_id) continue;
    pushToBucket(pagesByGroupId, page.ui_group_id, page);
  }
  // Add base-only pages (deleted sheets) into their base group.
  if (basePages) {
    for (const page of basePages) {
      if (!page.id || !page.ui_group_id) continue;
      if (headPageIds.has(page.id)) continue;
      pushToBucket(pagesByGroupId, page.ui_group_id, page);
    }
  }

  const childGroupsByParentGroupId = new Map<string, SchematicPageGroup[]>();
  const rootGroups: SchematicPageGroup[] = [];
  for (const group of Object.values(mergedPageGroups)) {
    if (group.parent) {
      pushToBucket(childGroupsByParentGroupId, group.parent.id, group);
    } else {
      rootGroups.push(group);
    }
  }

  const buildNode = (group: SchematicPageGroup, ancestors: ReadonlySet<string>): SchematicHierarchyGroup => {
    const pageBucket = pagesByGroupId.get(group.id) ?? [];
    const pageIdsInGroup = new Set(pageBucket.map((p) => p.id));

    // Cycle defense: skip any child whose id is already on the ancestor chain.
    const directChildren = (childGroupsByParentGroupId.get(group.id) ?? [])
      .filter((c) => !ancestors.has(c.id));

    const childrenByPageId = new Map<string, SchematicPageGroup[]>();
    const unanchored: SchematicPageGroup[] = [];
    for (const child of directChildren) {
      const parentPageId = child.parent?.reference_in_parent?.parent_page_id ?? null;
      if (parentPageId && pageIdsInGroup.has(parentPageId)) {
        pushToBucket(childrenByPageId, parentPageId, child);
      } else {
        unanchored.push(child);
      }
    }

    const nextAncestors = new Set(ancestors);
    nextAncestors.add(group.id);

    const pages: SchematicHierarchyPage[] = pageBucket.map((page) => ({
      pageId: page.id,
      name: page.name,
      mergedPageIds: [] as string[],
      instanceLabel: undefined as string | undefined,
      deletedFromHead: !headPageIds.has(page.id),
      childGroups: (childrenByPageId.get(page.id) ?? []).map((c) => buildNode(c, nextAncestors)),
    }));

    return {
      groupId: group.id,
      displayName: group.display_name,
      hideGroupNodes,
      originId: group.origin_id || null,
      pages,
      unanchoredChildGroups: unanchored.map((c) => buildNode(c, nextAncestors)),
    };
  };

  return rootGroups.map((g) => buildNode(g, new Set()));
}

/**
 * Lift every group to one level, per crysknife's `FlatGroups` hint, then combine
 * the groups that share an `origin_id` — the schema's own guidance, since those
 * are instances of one hierarchical block. Pages stay with their group; only the
 * nesting between groups is removed.
 *
 * Order is depth-first, so a group still follows the one it was nested under.
 * Groups without an `origin_id` are never combined.
 */
export function flattenGroups(
  groups: readonly SchematicHierarchyGroup[],
): SchematicHierarchyGroup[] {
  const flattened: SchematicHierarchyGroup[] = [];
  // Which root each group descends from, so instances only combine within the
  // schematic they belong to.
  const rootOf = new Map<string, string>();

  const positionKey = (
    group: SchematicHierarchyGroup,
    page: SchematicHierarchyPage,
    counters: Map<string, number>,
  ): string => {
    const label = page.instanceLabel ?? `${group.displayName}\u0000${page.name}`;
    // A sheet the diff removed is not the sheet that is still there, so the two
    // never share a row — one row cannot carry both states at once.
    const side = page.deletedFromHead ? 'deleted' : 'live';
    const seen = (counters.get(`${label}\u0000${side}`) ?? 0) + 1;
    counters.set(`${label}\u0000${side}`, seen);
    return `${label}\u0000${side}\u0000${seen}`;
  };

  const visit = (group: SchematicHierarchyGroup, rootId: string): void => {
    // A block's instance name lives on the group (TILE-1, TILE-2) while every
    // instance's page carries the block's own name (TILE). Combining instances
    // would leave a column of identical rows, so a single-page instance lends
    // its name to its page and takes the block's name for itself — that swap is
    // the only thing telling the instances apart.
    //
    // Only an instance does this. A group with no origin is a schematic in its
    // own right, and giving its name to its sheet would lose the sheet's.
    // An instance is "single" by the sheets it still has, not by every sheet in
    // the group: a sheet the diff deleted sits there beside the live ones, and
    // counting it would read the instance as something else, leave it
    // unlabelled, and strand it when its siblings combine. An instance the diff
    // removed outright has no live sheet at all and is judged on what it had.
    const livePages = group.pages.filter((page) => !page.deletedFromHead);
    const shapingPages = livePages.length > 0 ? livePages : group.pages;
    // An unnamed group has nothing to lend, and an empty label would leave the
    // row blank rather than falling back to the sheet's name.
    const isSingleInstance = group.originId !== null &&
      group.displayName !== '' &&
      shapingPages.length === 1;

    flattened.push({
      ...group,
      displayName: isSingleInstance ? shapingPages[0].name : group.displayName,
      pages: group.pages.map((page): SchematicHierarchyPage => ({
        ...page,
        instanceLabel: isSingleInstance ? group.displayName : page.instanceLabel,
        mergedPageIds: Array.from(page.mergedPageIds),
        childGroups: [],
      })),
      unanchoredChildGroups: [],
    });

    rootOf.set(group.groupId, rootId);

    for (const page of group.pages) {
      for (const child of page.childGroups) visit(child, rootId);
    }
    for (const child of group.unanchoredChildGroups) visit(child, rootId);
  };

  for (const group of groups) visit(group, group.groupId);

  const combined: SchematicHierarchyGroup[] = [];
  // Each row keeps the positions it has already taken. A page is keyed once, by
  // the group it arrived with, and that key is kept: re-deriving it later against
  // whichever group won the row would read a page under a name it never had, and
  // make the folding depend on the order the instances happened to arrive in.
  const byOrigin = new Map<string, {
    group: SchematicHierarchyGroup;
    shownAt: Map<string, SchematicHierarchyPage>;
  }>();

  for (const group of flattened) {
    if (group.originId === null) {
      combined.push(group);
      continue;
    }

    const originKey = `${rootOf.get(group.groupId) ?? ''}\u0000${group.originId}`;
    const existing = byOrigin.get(originKey);
    if (existing) {
      // The same position under a different parent shares a row, so a block
      // repeated four times reads as one set of sheets. The row carries the
      // pages it stands for, and whatever shows its changes shows theirs.
      const incomingCounts = new Map<string, number>();
      for (const page of group.pages) {
        const posKey = positionKey(group, page, incomingCounts);
        const shown = existing.shownAt.get(posKey);

        if (!shown) {
          existing.shownAt.set(posKey, page);
          existing.group.pages.push(page);
          continue;
        }

        shown.mergedPageIds.push(page.pageId, ...page.mergedPageIds);
      }
      continue;
    }

    const shownAt = new Map<string, SchematicHierarchyPage>();
    const ownCounts = new Map<string, number>();
    for (const page of group.pages) {
      shownAt.set(positionKey(group, page, ownCounts), page);
    }
    byOrigin.set(originKey, {group, shownAt});
    combined.push(group);
  }

  return combined;
}
